import {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {Alert, Button, Skeleton, Typography} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {makeSelectTabOperations} from 'redux/store/auth';
import {
  GET_INSTITUTIONAL_ACCOUNT_BY_ID,
  SET_KYB_STATUS,
  SUBMIT_KYB_FOR_REVIEW,
} from './resolvers';
import {isLikelyAccountId, unwrap} from './kybUtils';
import {useKybDocumentPreview} from './useKybDocumentPreview';
import {KybAccountHeader} from './detail/KybAccountHeader';
import {KybRequirementsChecklist} from './detail/KybRequirementsChecklist';
import {KybBeneficialOwners} from './detail/KybBeneficialOwners';
import {KybBusinessProfile} from './detail/KybBusinessProfile';
import {SubmitForReviewModal} from './detail/SubmitForReviewModal';
import {KybStatusHistory} from './detail/KybStatusHistory';
import {SetKybStatusModal} from './detail/SetKybStatusModal';
import {KycHubPanel} from 'pages/kycHub/KycHubPanel';
import {SUBJECT_PERMISSION_TAB} from 'pages/kycHub/kycHubUtils';
import {ComplianceRiskCard} from 'pages/kycHub/ComplianceRiskCard';
import styles from './kybDashboard.module.scss';

const {Text} = Typography;

/**
 * One institutional account: identity, beneficial owners, the requirement
 * checklist, the reviewer trail, and the KYC Hub (documents, internal notes,
 * activity).
 *
 * The Hub's documents tab is this account's document list — it reads the same
 * records as `account.documents`, so there is no second list here. Every
 * document action and the status decision refetch the account rather than
 * patching the cache. Rejecting a document flips its checklist row back to
 * unsatisfied server-side, and that recomputation is the whole reason a
 * bounced submission asks for exactly the right re-uploads — reading it back
 * is cheaper than trying to mirror the rule here and getting it subtly wrong.
 */
export const KybAccountDetail = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState(null);

  // The same KYB permission that gates the KYC Hub's document actions.
  const operations = useSelector(state =>
    makeSelectTabOperations(state, SUBJECT_PERMISSION_TAB.INSTITUTIONAL),
  );
  const canUpdate = Boolean(operations?.update);

  const idLooksValid = isLikelyAccountId(id);

  const {data, loading, error, refetch} = useQuery(
    GET_INSTITUTIONAL_ACCOUNT_BY_ID,
    {
      variables: {id},
      fetchPolicy: 'cache-and-network',
      skip: !idLooksValid,
    },
  );

  const [setKybStatus, {loading: deciding}] = useMutation(SET_KYB_STATUS);
  const [submitKybForReview, {loading: submittingForReview}] = useMutation(
    SUBMIT_KYB_FOR_REVIEW,
  );

  const {openDocument, canOpen, openingId} = useKybDocumentPreview();

  const {data: account, error: responseError} = unwrap(
    data?.getInstitutionalAccountById,
  );

  const handleDecisionSubmit = async input => {
    try {
      const {data: result} = await setKybStatus({
        variables: {input: {accountId: id, ...input}},
        refetchQueries: ['KycHubActivity'],
      });
      const {data: updated, error: decisionError} = unwrap(
        result?.setKybStatus,
      );
      if (decisionError) {
        toast.error(decisionError);
        return;
      }
      toast.success(
        updated?.isFullyActivated
          ? 'Approved. The account is now live.'
          : 'Verification updated. The business has been emailed.',
      );
      setDecision(null);
      await refetch();
    } catch (requestError) {
      toast.error('Could not update this verification.');
    }
  };

  /**
   * `submitted: false` is an answer, not a failure: the checklist is still
   * incomplete, and the rows it names are shown on the header until dismissed.
   */
  const handleSubmitForReview = async () => {
    try {
      const {data: result} = await submitKybForReview({
        variables: {accountId: id},
        refetchQueries: ['KycHubActivity'],
      });
      const {data: submission, error: submitError} = unwrap(
        result?.submitKybForReview,
      );
      if (submitError) {
        toast.error(submitError);
        return;
      }
      setConfirmingSubmit(false);
      if (!submission?.submitted) {
        const missing = submission?.missingRequirements || [];
        setMissingRequirements(missing);
        toast.warning(
          missing.length
            ? 'Not submitted. Some required items are still missing.'
            : 'Not submitted.',
        );
        return;
      }
      setMissingRequirements(null);
      toast.success('Submitted for review. The business has been emailed.');
      await refetch();
    } catch (requestError) {
      toast.error('Could not submit this verification.');
    }
  };

  const message = !idLooksValid
    ? 'That is not a valid account link. Open the account from the queue.'
    : responseError ||
      (error && !account ? 'Could not load this account.' : null);

  return (
    <div className={styles.shell}>
      <Button
        type="link"
        className={styles.backLink}
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/dashboard/kyb-dashboard')}>
        Back to the queue
      </Button>

      {message && (
        <Alert
          type="error"
          showIcon
          className={styles.alert}
          message={message}
        />
      )}

      {loading && !account && idLooksValid ? (
        <Skeleton active paragraph={{rows: 8}} />
      ) : !account ? (
        !message && <Text type="secondary">This account was not found.</Text>
      ) : (
        <>
          <KybAccountHeader
            account={account}
            onDecision={setDecision}
            onSubmitForReview={
              canUpdate ? () => setConfirmingSubmit(true) : undefined
            }
            missingRequirements={missingRequirements}
            onDismissMissing={() => setMissingRequirements(null)}
          />

          <div className={styles.detailGrid}>
            <div className={styles.detailColumn}>
              <KybRequirementsChecklist
                requirements={account.kybRequirements}
                documents={account.documents}
              />
              <KycHubPanel
                key={account.id}
                subjectType="INSTITUTIONAL"
                subjectId={account.id}
                kyb={{
                  kybStatus: account.kybStatus,
                  requirements: account.kybRequirements,
                  beneficialOwners: account.beneficialOwners,
                }}
              />
            </div>

            <div className={styles.detailColumn}>
              <ComplianceRiskCard
                key={account.id}
                subjectType="INSTITUTIONAL"
                subjectId={account.id}
              />
              <KybBusinessProfile account={account} />
              <KybBeneficialOwners
                account={account}
                onPreview={openDocument}
                canPreview={canOpen}
                previewingId={openingId}
              />
              <KybStatusHistory account={account} />
            </div>
          </div>
        </>
      )}

      <SetKybStatusModal
        open={Boolean(decision)}
        status={decision}
        submitting={deciding}
        onClose={() => setDecision(null)}
        onSubmit={handleDecisionSubmit}
      />

      <SubmitForReviewModal
        open={confirmingSubmit}
        entityName={account?.entityName}
        submitting={submittingForReview}
        onClose={() => setConfirmingSubmit(false)}
        onSubmit={handleSubmitForReview}
      />
    </div>
  );
};
